import React, { useState } from 'react';
import TeslaRobot from '../components/TeslaRobot';
import { Link } from 'react-router-dom';

const ApiPage: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'overview' | 'endpoints' | 'authentication' | 'examples'>('overview');

  return (
    <div className="api-page">
      <div className="api-header">
        <div className="api-title-container">
          <h1 className="api-title">
            API <span className="text-gradient">Documentation</span>
          </h1>
          <p className="api-subtitle">
            Integrate Digital Workplace AI employees with your custom systems
          </p>
        </div>
        <div className="api-robot">
          <TeslaRobot width={100} height={100} />
        </div>
      </div>

      <div className="api-content">
        <div className="api-tabs">
          <button
            className={`api-tab ${activeTab === 'overview' ? 'active' : ''}`}
            onClick={() => setActiveTab('overview')}
          >
            Overview
          </button>
          <button
            className={`api-tab ${activeTab === 'authentication' ? 'active' : ''}`}
            onClick={() => setActiveTab('authentication')}
          >
            Authentication
          </button>
          <button
            className={`api-tab ${activeTab === 'endpoints' ? 'active' : ''}`}
            onClick={() => setActiveTab('endpoints')}
          >
            Endpoints
          </button>
          <button
            className={`api-tab ${activeTab === 'examples' ? 'active' : ''}`}
            onClick={() => setActiveTab('examples')}
          >
            Examples
          </button>
        </div>

        <div className="api-tab-content">
          {activeTab === 'overview' && (
            <div className="api-overview">
              <div className="api-section">
                <h2>Integration Overview</h2>
                <p>
                  Digital Workplace provides a comprehensive RESTful API that allows you to seamlessly integrate
                  our AI employees with your existing systems and workflows. Our API enables you to:
                </p>
                <ul className="api-features-list">
                  <li>
                    <span className="api-feature-icon">🔄</span>
                    <span className="api-feature-text">
                      <strong>Access AI Employees:</strong> Programmatically access and control your digital employees
                    </span>
                  </li>
                  <li>
                    <span className="api-feature-icon">💬</span>
                    <span className="api-feature-text">
                      <strong>Neural Chat Integration:</strong> Embed AI conversations directly in your applications
                    </span>
                  </li>
                  <li>
                    <span className="api-feature-icon">🔍</span>
                    <span className="api-feature-text">
                      <strong>Skill-Based Routing:</strong> Automatically route tasks to the most suitable AI employee
                    </span>
                  </li>
                  <li>
                    <span className="api-feature-icon">📊</span>
                    <span className="api-feature-text">
                      <strong>Analytics & Reporting:</strong> Retrieve performance metrics and usage statistics
                    </span>
                  </li>
                  <li>
                    <span className="api-feature-icon">🔐</span>
                    <span className="api-feature-text">
                      <strong>Secure Data Exchange:</strong> Safely transfer data between your systems and our platform
                    </span>
                  </li>
                </ul>
              </div>

              <div className="api-section">
                <h2>Getting Started</h2>
                <p>
                  To begin integrating with our API, you'll need to:
                </p>
                <ol className="api-steps">
                  <li>
                    <h3>Create an API Key</h3>
                    <p>
                      Register for an API key through your Digital Workplace dashboard. This key will be used to
                      authenticate your requests.
                    </p>
                  </li>
                  <li>
                    <h3>Choose Your Integration Method</h3>
                    <p>
                      Decide whether you'll use our REST API directly, or leverage one of our client libraries
                      available for JavaScript, Python, Java, and .NET.
                    </p>
                  </li>
                  <li>
                    <h3>Configure Webhooks (Optional)</h3>
                    <p>
                      Set up webhooks to receive real-time notifications when specific events occur within your
                      digital workforce.
                    </p>
                  </li>
                  <li>
                    <h3>Test in Sandbox</h3>
                    <p>
                      Use our sandbox environment to test your integration before moving to production.
                    </p>
                  </li>
                </ol>
              </div>

              <div className="api-cta">
                <h3>Ready to integrate?</h3>
                <p>
                  Explore our detailed documentation or contact our integration specialists for personalized support.
                </p>
                <div className="api-cta-buttons">
                  <button
                    className="api-cta-button primary"
                    onClick={() => setActiveTab('endpoints')}
                  >
                    View API Endpoints
                  </button>
                  <Link to="/contact" className="api-cta-button secondary">
                    Contact Support
                  </Link>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'authentication' && (
            <div className="api-authentication">
              <div className="api-section">
                <h2>Authentication</h2>
                <p>
                  Digital Workplace API uses API keys for authentication. All API requests must include your API key
                  in the request header.
                </p>

                <div className="code-block">
                  <div className="code-header">
                    <span className="code-title">API Key Authentication</span>
                    <button className="copy-button">Copy</button>
                  </div>
                  <pre className="code-content">
                    <code>
{`// Example API request with authentication header
fetch('https://api.digitalworkplace.com/v1/employees', {
  method: 'GET',
  headers: {
    'Content-Type': 'application/json',
    'X-API-Key': 'YOUR_API_KEY'
  }
})
.then(response => response.json())
.then(data => {});`}
                    </code>
                  </pre>
                </div>

                <h3>Obtaining an API Key</h3>
                <p>
                  To obtain an API key:
                </p>
                <ol>
                  <li>Log in to your Digital Workplace dashboard</li>
                  <li>Navigate to Settings &gt; API Keys</li>
                  <li>Click "Generate New API Key"</li>
                  <li>Name your key and set appropriate permissions</li>
                  <li>Store your key securely - it will only be shown once</li>
                </ol>

                <div className="api-warning">
                  <h4>Security Notice</h4>
                  <p>
                    Never expose your API key in client-side code. Always make API calls from your server
                    and implement proper security measures to protect your key.
                  </p>
                </div>
              </div>

              <div className="api-section">
                <h2>Rate Limiting</h2>
                <p>
                  To ensure fair usage and system stability, the Digital Workplace API implements rate limiting:
                </p>
                <ul>
                  <li><strong>Standard Plan:</strong> 100 requests per minute</li>
                  <li><strong>Professional Plan:</strong> 500 requests per minute</li>
                  <li><strong>Enterprise Plan:</strong> Custom limits based on your needs</li>
                </ul>
                <p>
                  If you exceed your rate limit, the API will return a 429 Too Many Requests response. The response
                  will include a Retry-After header indicating how long to wait before making another request.
                </p>
              </div>
            </div>
          )}

          {activeTab === 'endpoints' && (
            <div className="api-endpoints">
              <div className="api-section">
                <h2>API Endpoints</h2>
                <p>
                  The Digital Workplace API provides the following endpoints for integrating with your systems:
                </p>

                <div className="endpoint-group">
                  <h3>Employee Management</h3>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/employees</div>
                    <div className="endpoint-description">List all available digital employees</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/employees/{'{id}'}</div>
                    <div className="endpoint-description">Get details for a specific employee</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method post">POST</div>
                    <div className="endpoint-path">/v1/employees</div>
                    <div className="endpoint-description">Create a new digital employee</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method put">PUT</div>
                    <div className="endpoint-path">/v1/employees/{'{id}'}</div>
                    <div className="endpoint-description">Update an existing employee</div>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>Neural Chat</h3>
                  <div className="endpoint">
                    <div className="endpoint-method post">POST</div>
                    <div className="endpoint-path">/v1/chat/{'{employee_id}'}/messages</div>
                    <div className="endpoint-description">Send a message to a digital employee</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/chat/{'{employee_id}'}/conversations</div>
                    <div className="endpoint-description">List all conversations with an employee</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/chat/conversations/{'{conversation_id}'}</div>
                    <div className="endpoint-description">Get conversation history</div>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>Skills & Categories</h3>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/skills</div>
                    <div className="endpoint-description">List all available skills</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/categories</div>
                    <div className="endpoint-description">List all employee categories</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/industries</div>
                    <div className="endpoint-description">List all industry specializations</div>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>Analytics & Reporting</h3>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/analytics/usage</div>
                    <div className="endpoint-description">Get usage statistics for your account</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/analytics/employees/{'{id}'}/performance</div>
                    <div className="endpoint-description">Get performance metrics for an employee</div>
                  </div>
                </div>

                <div className="endpoint-group">
                  <h3>Webhooks</h3>
                  <div className="endpoint">
                    <div className="endpoint-method post">POST</div>
                    <div className="endpoint-path">/v1/webhooks</div>
                    <div className="endpoint-description">Create a new webhook subscription</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method get">GET</div>
                    <div className="endpoint-path">/v1/webhooks</div>
                    <div className="endpoint-description">List all webhook subscriptions</div>
                  </div>
                  <div className="endpoint">
                    <div className="endpoint-method delete">DELETE</div>
                    <div className="endpoint-path">/v1/webhooks/{'{id}'}</div>
                    <div className="endpoint-description">Delete a webhook subscription</div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeTab === 'examples' && (
            <div className="api-examples">
              <div className="api-section">
                <h2>Integration Examples</h2>
                <p>
                  Below are examples of how to integrate Digital Workplace AI employees with various systems:
                </p>

                <div className="example-tabs">
                  <button className="example-tab active">JavaScript</button>
                  <button className="example-tab">Python</button>
                  <button className="example-tab">Java</button>
                  <button className="example-tab">.NET</button>
                </div>

                <div className="code-block">
                  <div className="code-header">
                    <span className="code-title">Fetching Available Employees</span>
                    <button className="copy-button">Copy</button>
                  </div>
                  <pre className="code-content">
                    <code>
{`// JavaScript example using fetch API
const API_KEY = 'your_api_key';
const BASE_URL = 'https://api.digitalworkplace.com/v1';

// Get all available employees
async function getEmployees() {
  try {
    const response = await fetch(\`\${BASE_URL}/employees\`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      }
    });

    if (!response.ok) {
      throw new Error(\`API error: \${response.status}\`);
    }

    const data = await response.json();
    return data.employees;
  } catch (error) {
    console.error('Error fetching employees:', error);
    return [];
  }
}

// Example usage
getEmployees().then(employees => {
  console.log(\`Found \${employees.length} employees\`);
  employees.forEach(employee => {
    console.log(\`\${employee.name}: \${employee.skills.join(', ')}\`);
  });
});`}
                    </code>
                  </pre>
                </div>

                <div className="code-block">
                  <div className="code-header">
                    <span className="code-title">Sending a Message to an AI Employee</span>
                    <button className="copy-button">Copy</button>
                  </div>
                  <pre className="code-content">
                    <code>
{`// JavaScript example of neural chat integration
async function sendMessage(employeeId, message) {
  try {
    const response = await fetch(\`\${BASE_URL}/chat/\${employeeId}/messages\`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        message: message,
        conversation_id: null // null creates a new conversation
      })
    });

    if (!response.ok) {
      throw new Error(\`API error: \${response.status}\`);
    }

    const data = await response.json();
    return data.response;
  } catch (error) {
    console.error('Error sending message:', error);
    return null;
  }
}

// Example usage in a chat application
document.getElementById('send-button').addEventListener('click', async () => {
  const messageInput = document.getElementById('message-input');
  const chatContainer = document.getElementById('chat-container');
  const employeeId = '123'; // Replace with actual employee ID

  // Add user message to chat
  const userMessage = messageInput.value;
  chatContainer.innerHTML += \`<div class="user-message">\${userMessage}</div>\`;
  messageInput.value = '';

  // Get AI response
  const response = await sendMessage(employeeId, userMessage);

  if (response) {
    // Add AI response to chat
    chatContainer.innerHTML += \`<div class="ai-message">\${response.text}</div>\`;
  }
});`}
                    </code>
                  </pre>
                </div>

                <div className="code-block">
                  <div className="code-header">
                    <span className="code-title">Setting Up a Webhook</span>
                    <button className="copy-button">Copy</button>
                  </div>
                  <pre className="code-content">
                    <code>
{`// JavaScript example of creating a webhook subscription
async function createWebhook(eventType, callbackUrl) {
  try {
    const response = await fetch(\`\${BASE_URL}/webhooks\`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-API-Key': API_KEY
      },
      body: JSON.stringify({
        event_type: eventType, // e.g., 'message.received', 'task.completed'
        callback_url: callbackUrl,
        active: true
      })
    });

    if (!response.ok) {
      throw new Error(\`API error: \${response.status}\`);
    }

    const data = await response.json();
    console.log(\`Webhook created with ID: \${data.webhook_id}\`);
    return data.webhook_id;
  } catch (error) {
    console.error('Error creating webhook:', error);
    return null;
  }
}

// Example usage
createWebhook(
  'message.received',
  'https://your-app.com/api/webhooks/digital-workplace'
);

// Example webhook handler (server-side code)
app.post('/api/webhooks/digital-workplace', (req, res) => {
  const webhookData = req.body;

  // Verify webhook signature (recommended for security)
  if (!verifyWebhookSignature(req)) {
    return res.status(401).send('Invalid signature');
  }

  // Process webhook data

  // Process based on event type
  switch (webhookData.event_type) {
    case 'message.received':
      // Handle new message
      processNewMessage(webhookData.data);
      break;
    case 'task.completed':
      // Handle completed task
      updateTaskStatus(webhookData.data);
      break;
    // Handle other event types
  }

  // Acknowledge receipt
  res.status(200).send('Webhook received');
});`}
                    </code>
                  </pre>
                </div>
              </div>

              <div className="api-section">
                <h2>Common Integration Scenarios</h2>

                <div className="integration-scenario">
                  <h3>CRM Integration</h3>
                  <p>
                    Integrate Digital Workplace AI employees with your CRM system to automate customer interactions,
                    generate reports, and provide instant support.
                  </p>
                  <div className="scenario-steps">
                    <div className="scenario-step">
                      <div className="step-number">1</div>
                      <div className="step-content">
                        <h4>Connect via API</h4>
                        <p>Use our API to connect to your CRM system (Salesforce, HubSpot, etc.)</p>
                      </div>
                    </div>
                    <div className="scenario-step">
                      <div className="step-number">2</div>
                      <div className="step-content">
                        <h4>Configure Triggers</h4>
                        <p>Set up events that will trigger AI employee actions</p>
                      </div>
                    </div>
                    <div className="scenario-step">
                      <div className="step-number">3</div>
                      <div className="step-content">
                        <h4>Map Data Fields</h4>
                        <p>Define how data is shared between systems</p>
                      </div>
                    </div>
                    <div className="scenario-step">
                      <div className="step-number">4</div>
                      <div className="step-content">
                        <h4>Test & Deploy</h4>
                        <p>Validate the integration and deploy to production</p>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="integration-scenario">
                  <h3>Custom Web Application</h3>
                  <p>
                    Embed AI employees directly into your web application to provide real-time assistance to users.
                  </p>
                  <div className="code-block">
                    <div className="code-header">
                      <span className="code-title">Embedding Neural Chat</span>
                      <button className="copy-button">Copy</button>
                    </div>
                    <pre className="code-content">
                      <code>
{`<!-- HTML -->
<div id="digital-workplace-chat"></div>

<!-- JavaScript -->
<script src="https://cdn.digitalworkplace.com/js/chat-widget.js"></script>
<script>
  DigitalWorkplace.initChat({
    container: '#digital-workplace-chat',
    apiKey: 'YOUR_PUBLIC_API_KEY',
    employeeId: '123',
    theme: 'light',
    welcomeMessage: 'How can I help you today?',
    position: 'bottom-right'
  });
</script>`}
                      </code>
                    </pre>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ApiPage;
